import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Mail, MapPin, ArrowUpRight, Facebook, Linkedin, Link2, Check } from "lucide-react";
import api from "@/lib/api";

const SOCIAL_LINKS = [
    { icon: Instagram, label: "Instagram", href: "https://www.instagram.com/silvertree_designstudio/" },
    { icon: Facebook, label: "Facebook", href: "https://www.facebook.com/StudioSilverTree1176" },
    { icon: Linkedin, label: "LinkedIn", href: "https://www.linkedin.com/company/silvertree-communication-design-studio" },
    { icon: Link2, label: "Linktree", href: "https://linktr.ee/silvertree1176" },
];

const NewsletterForm = () => {
    const [email, setEmail] = useState("");
    const [status, setStatus] = useState({ state: "idle", msg: "" });

    const submit = async (e) => {
        e.preventDefault();
        setStatus({ state: "loading", msg: "" });
        try {
            const { data } = await api.post("/newsletter/subscribe", { email });
            setStatus({ state: "success", msg: data.message });
            setEmail("");
        } catch (err) {
            const detail = err.response?.data?.detail;
            const msg = typeof detail === "string" ? detail : "Something went wrong. Please try again.";
            setStatus({ state: "error", msg });
        }
    };

    return (
        <form data-testid="newsletter-form" onSubmit={submit} className="max-w-md">
            <div className="flex items-stretch gap-3">
                <input
                    type="email"
                    required
                    data-testid="newsletter-email-input"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="flex-1 bg-transparent border-b border-[rgba(235,233,222,0.35)] py-2 text-sm text-[var(--st-cream)] placeholder:text-[#8f8c7c] focus:outline-none focus:border-[var(--st-gold)] transition-colors"
                />
                <button
                    type="submit"
                    disabled={status.state === "loading"}
                    data-testid="newsletter-submit-button"
                    className="shrink-0 text-[var(--st-cream)] hover:text-[var(--st-gold)] transition-colors disabled:opacity-50"
                    aria-label="Subscribe"
                >
                    {status.state === "success" ? <Check size={18} /> : <ArrowUpRight size={18} />}
                </button>
            </div>
            {status.msg && (
                <p
                    data-testid="newsletter-status"
                    className={`mt-3 text-xs ${status.state === "success" ? "text-[var(--st-gold)]" : "text-red-400"}`}
                >
                    {status.msg}
                </p>
            )}
        </form>
    );
};

const Footer = () => {
    return (
        <footer data-testid="site-footer" className="section-dark relative z-10">
            <div className="st-container py-20 lg:py-28">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                    {/* Word mark + manifesto */}
                    <div className="lg:col-span-5">
                        <p className="eyebrow mb-6">SilverTree · Est. 2010</p>
                        <h2 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[0.95] mb-8">
                            Design.<br />
                            Consult.<br />
                            <span className="italic text-[var(--st-gold)]">Learn.</span>
                        </h2>
                        <p className="text-sm text-[#c8c5b3] max-w-md leading-relaxed mb-8">
                            A boutique design studio working across India and Malaysia,
                            building considered brands for founders who want to be remembered.
                        </p>
                        <p className="eyebrow mb-3 opacity-70">Notes from the studio, monthly</p>
                        <NewsletterForm />
                    </div>

                    {/* Sitemap */}
                    <div className="lg:col-span-3 lg:col-start-7">
                        <p className="eyebrow mb-5 opacity-70">Studio</p>
                        <ul className="space-y-3 text-base">
                            <li><Link to="/design" className="link-underline">Design</Link></li>
                            <li><Link to="/consult" className="link-underline">Consult</Link></li>
                            <li><Link to="/learn" className="link-underline">Learn</Link></li>
                            <li><Link to="/work" className="link-underline">Work</Link></li>
                            <li><Link to="/about" className="link-underline">About</Link></li>
                            <li><Link to="/blog" className="link-underline">Journal</Link></li>
                            <li><Link to="/contact" className="link-underline">Contact</Link></li>
                        </ul>
                    </div>

                    {/* Contact + locations */}
                    <div className="lg:col-span-3">
                        <p className="eyebrow mb-5 opacity-70">Find us</p>
                        <div className="space-y-5 text-sm leading-relaxed">
                            <div>
                                <div className="flex items-center gap-2 mb-1 text-[var(--st-gold)]">
                                    <MapPin size={14} /> Gurgaon
                                </div>
                                <p className="text-[#c8c5b3]">Sector 48, Sohna Road<br />Gurgaon, Haryana, India</p>
                            </div>
                            <div>
                                <div className="flex items-center gap-2 mb-1 text-[var(--st-gold)]">
                                    <MapPin size={14} /> Kuala Lumpur
                                </div>
                                <p className="text-[#c8c5b3]">Mont Kiara, Jalan Dutamas<br />Kuala Lumpur, Malaysia</p>
                            </div>
                            <div className="pt-2 space-y-3">
                                <a
                                    href="mailto:sugandhi@thesilver-tree.com"
                                    data-testid="footer-email"
                                    className="flex items-center gap-2 link-underline"
                                >
                                    <Mail size={14} /> sugandhi@thesilver-tree.com
                                </a>
                                <div className="pt-2">
                                    <p className="eyebrow opacity-70 mb-3">Follow the studio</p>
                                    <div className="flex items-center gap-3">
                                        {SOCIAL_LINKS.map((s) => (
                                            <a
                                                key={s.label}
                                                href={s.href}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                data-testid={`footer-social-${s.label.toLowerCase()}`}
                                                aria-label={s.label}
                                                className="w-10 h-10 border border-[rgba(235,233,222,0.25)] flex items-center justify-center text-[var(--st-cream)] hover:bg-[var(--st-gold)] hover:border-[var(--st-gold)] hover:text-[var(--st-ink)] transition-colors"
                                            >
                                                <s.icon size={16} />
                                            </a>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <hr className="thin my-12" />

                <div className="flex flex-col md:flex-row gap-6 md:items-center justify-between text-xs uppercase tracking-widest text-[#a09e8f]">
                    <p>© 2026 SilverTree Design Studio. All rights reserved.</p>
                    <div className="flex items-center gap-6">
                        <Link to="/contact" data-testid="footer-cta" className="text-[var(--st-cream)] inline-flex items-center gap-1.5">
                            Start a project <ArrowUpRight size={14} />
                        </Link>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
